import type { ServiceFetch } from "../../../services/serviceFetch";

/* ── Domain types (mirrors MyQueue API payload shape) ── */

export interface MyQueueTask {
  id: number;
  jobNo?: string;
  jobTask?: string;
  title?: string;
  requestedBy?: string;
  site?: string;
  displayDueDate?: string;
  dueDate?: string;
  displayScheduledDate?: string;
  displayStartedDate?: string;
  displayCompletedDate?: string;
  estimatedHours?: number | string;
  started?: boolean;
  completed?: boolean;
  scheduled?: boolean;
  status?: string;
  vip?: boolean;
  rush?: boolean;
  priority?: boolean;
  vipNotes?: string;
  office?: string;
  [key: string]: unknown;
}

export interface MyQueueTaskListData {
  tasks?: MyQueueTask[];
  inProgressTasks?: MyQueueTask[];
  count?: number;
}

export interface MyQueueTaskListResponse {
  success?: boolean;
  message?: string;
  data?: MyQueueTaskListData;
}

export interface MyQueueTaskDetailData {
  instructions?: string;
  taskInstructions?: string;
  billingCode1?: string;
  billingCode2?: string;
  comments?: string;
  taskNotes?: Array<{ createdBy?: string; created?: string; comments?: string }>;
}

export interface MyQueueTaskDetailResponse {
  success?: boolean;
  message?: string;
  data?: MyQueueTaskDetailData;
}

export interface MyQueueTimeclockEntry {
  tcitype?: string;
  action?: string;
  displayStartTime?: string;
  displayEndTime?: string;
  startTime?: string;
  endTime?: string;
  hours?: number;
}

export interface MyQueueTimeclockData {
  lastAction?: string;
  lastTime?: string;
  userName?: string;
  role?: string;
  timeclockEntries?: MyQueueTimeclockEntry[];
}

export interface MyQueueTimeclockResponse {
  success?: boolean;
  message?: string;
  data?: MyQueueTimeclockData;
}

export interface MyQueueLocation {
  siteTimeclockLocationId: number;
  description?: string;
}

export interface MyQueueUserDetailsRole {
  userRole?: string;
  isLocationTrackingEnabled?: boolean;
}

export interface MyQueueUserDetails {
  id?: number;
  rguid?: string;
  site?: string;
  userName?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  staffId?: string;
  timezoneid?: string;
  dateTimeFormat?: string;
  locations?: MyQueueLocation[];
  role?: MyQueueUserDetailsRole;
  [key: string]: unknown;
}

export interface MyQueueUserDetailsResponse {
  success?: boolean;
  message?: string;
  data?: MyQueueUserDetails;
}

export interface MyQueueInvoiceItem {
  costCode?: string;
  quantity?: number;
  charge?: number;
}

export interface MyQueueInvoiceMonth {
  month?: string;
  displayMonth?: string;
  items?: MyQueueInvoiceItem[];
  subtotal?: { totalQuantity?: number; totalCharge?: number };
}

export interface MyQueueInvoiceData {
  invoicesByMonth?: MyQueueInvoiceMonth[];
  grandTotal?: { totalQuantity?: number; totalCharge?: number };
}

export interface MyQueueInvoiceResponse {
  success?: boolean;
  message?: string;
  data?: MyQueueInvoiceData;
}

export interface MyQueueQcProdTask {
  id: number;
  jobTask?: string;
  qcStatus?: string;
  firstName?: string;
  lastName?: string;
  displayProductionStartedAt?: string;
  displayProductionCompletedAt?: string;
  [key: string]: unknown;
}

export interface MyQueueQcInfoData {
  jobNo?: string;
  jobTask?: string;
  requestedBy?: string;
  office?: string;
  displayDueDate?: string;
  estimatedHours?: number | string;
  billingCodes?: string;
  noPages?: number;
  productionsTaskForQc?: MyQueueQcProdTask[];
  [key: string]: unknown;
}

export interface MyQueueQcInfoResponse {
  success?: boolean;
  message?: string;
  data?: MyQueueQcInfoData;
}

export interface MyQueueProdTimeBlock {
  firstName?: string;
  lastName?: string;
  displayStartTime?: string;
  displayStopTime?: string;
  actualhours?: number;
  [key: string]: unknown;
}

export interface MyQueueProdInstructions {
  requestInstructions?: string;
  taskInstructions?: string;
}

export interface MyQueueProdTaskData {
  jobNo?: string;
  jobTask?: string;
  jobType?: string;
  qcStatus?: string;
  displayStartTime?: string;
  displayStopTime?: string;
  totalActualHours?: number;
  instructions?: MyQueueProdInstructions;
  productionsTasks?: MyQueueProdTimeBlock[];
  [key: string]: unknown;
}

export interface MyQueueProdTaskResponse {
  success?: boolean;
  message?: string;
  data?: MyQueueProdTaskData;
}

export interface MyQueueFilterModel {
  started?: { filterType: "boolean"; type: "equals"; filter: boolean };
  completed?: { filterType: "boolean"; type: "equals"; filter: boolean };
}

export type MyQueueTaskAction = "start" | "pause" | "complete";
export type MyQueueTimeclockAction =
  | "punchin"
  | "punchout"
  | "lunch"
  | "break"
  | "return";

export interface MyQueueTaskSearchRequest {
  FilterColumn: string;
  FilterValue: string;
  IsAsc: boolean;
  PageNum: number;
  PageSize: number;
  Sites: string[];
  SortColumn: string;
  filterModel: MyQueueFilterModel;
  sortModel: Array<{ sort: "asc" | "desc"; colId: string }>;
  startRow: number;
  endRow: number;
}

interface JsonRequest {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
}

/* ── Client factory ──────────────────────────────── */

function buildUrl(base: string, path: string): string {
  if (!base) {
    throw new Error(
      "MyQueue API base URL is not configured on the connection (apiBaseUrl).",
    );
  }
  return base.replace(/\/$/, "") + path;
}

export interface MyQueueClient {
  searchTasks(req: MyQueueTaskSearchRequest): Promise<MyQueueTaskListResponse>;
  getTaskDetails(workflowId: number): Promise<MyQueueTaskDetailResponse>;
  taskAction(
    action: MyQueueTaskAction,
    jobTicketId: string,
    workflowId: number,
  ): Promise<{ success?: boolean; message?: string } | null>;
  updateTaskComment(
    workflowId: number,
    comments: string,
  ): Promise<{ success?: boolean; message?: string } | null>;
  getTimeclockEntries(): Promise<MyQueueTimeclockResponse>;
  getUserDetails(): Promise<MyQueueUserDetailsResponse>;
  postTimeclockAction(
    action: MyQueueTimeclockAction,
    siteTimeclockLocationId?: number,
  ): Promise<{ success?: boolean; message?: string } | null>;
  getQcInfo(workflowId: number): Promise<MyQueueQcInfoResponse>;
  getProdTask(prodTaskId: number): Promise<MyQueueProdTaskResponse>;
  setQcStatus(
    prodTaskId: number,
    body: { JobNo: string; JobTask: string; QcStatus: boolean },
  ): Promise<{ success?: boolean; message?: string } | null>;
  getInvoices(jobNo: string): Promise<MyQueueInvoiceResponse>;
}

export function createMyQueueClient(
  serviceFetch: ServiceFetch,
  apiBaseUrl: string,
): MyQueueClient {
  async function jsonCall<T>(path: string, init: JsonRequest = {}): Promise<T> {
    const method = init.method ?? "GET";
    const headers: Record<string, string> = {};
    let body: BodyInit | undefined;
    if (init.body !== undefined) {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(init.body);
    }
    const res = await serviceFetch("myqueue", {
      url: buildUrl(apiBaseUrl, path),
      method,
      headers,
      body,
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => "");
      throw new Error(`MyQueue ${method} ${path}: ${res.status} ${errText}`);
    }
    const text = await res.text();
    return (text ? JSON.parse(text) : null) as T;
  }

  return {
    searchTasks: (req) =>
      jsonCall<MyQueueTaskListResponse>("/tasks/search", {
        method: "POST",
        body: req,
      }),

    getTaskDetails: (workflowId) =>
      jsonCall<MyQueueTaskDetailResponse>("/tasks/moreinfo", {
        method: "POST",
        body: { workflowId },
      }),

    taskAction: (action, jobTicketId, workflowId) =>
      jsonCall<{ success?: boolean; message?: string } | null>(
        `/tasks/${action}`,
        { method: "POST", body: { jobTicketId, workflowId } },
      ),

    updateTaskComment: (workflowId, comments) =>
      jsonCall<{ success?: boolean; message?: string } | null>(
        "/tasks/update",
        { method: "PUT", body: { workflowId, comments } },
      ),

    getTimeclockEntries: () =>
      jsonCall<MyQueueTimeclockResponse>("/timeclock/entries"),

    getUserDetails: () =>
      jsonCall<MyQueueUserDetailsResponse>("/user/details"),

    postTimeclockAction: (action, siteTimeclockLocationId) => {
      // Every action (punchin/punchout/lunch/break/return) sends the same
      // { siteTimeclockLocationId } payload when location tracking is enabled;
      // fall back to the legacy { timeclockRequest: { action } } shape only
      // when no location is available.
      const body =
        typeof siteTimeclockLocationId === "number"
          ? { siteTimeclockLocationId }
          : { timeclockRequest: { action } };
      return jsonCall<{ success?: boolean; message?: string } | null>(
        `/timeclock/${action}`,
        { method: "PUT", body },
      );
    },

    getQcInfo: (workflowId) =>
      jsonCall<MyQueueQcInfoResponse>(`/tasks/qc/${workflowId}`),

    getProdTask: (prodTaskId) =>
      jsonCall<MyQueueProdTaskResponse>(`/tasks/prod/${prodTaskId}`),

    setQcStatus: (prodTaskId, body) =>
      jsonCall<{ success?: boolean; message?: string } | null>(
        `/tasks/prod/${prodTaskId}/qcstatus`,
        { method: "POST", body },
      ),

    getInvoices: (jobNo) =>
      jsonCall<MyQueueInvoiceResponse>(
        `/jobs/${encodeURIComponent(jobNo)}/invoices`,
      ),
  };
}
